<script setup>
  import { ref, computed, watch, onMounted, onUnmounted } from "vue";

  const props = defineProps({
    product: {
      type: Object,
      required: true,
    },
    isListView: {
      type: Boolean,
      default: false,
    },
    userEmail: {
      type: String,
      default: null,
    },
    isFavorite: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits([
    "add-to-cart",
    "notify-back-in-stock",
    "toggle-favorite",
  ]);

  const showModal = ref(false);
  const showAuthWarning = ref(false);
  const showFavToast = ref(false);
  const favToastMessage = ref("");
  const inputEmail = ref("");
  const isSubmitting = ref(false);
  const isNotified = ref(false);

  const isFav = ref(props.isFavorite);
  const isFavLoading = ref(false);

  const selectedVariant = ref(
    props.product.variants && props.product.variants.length > 0
      ? props.product.variants[0]
      : null,
  );

  const getCurrentProductId = () => {
    return selectedVariant.value ? selectedVariant.value.id : props.product.id;
  };

  const getStoredFavorites = () => {
    try {
      return JSON.parse(localStorage.getItem("user_favorites") || "[]");
    } catch {
      return [];
    }
  };

  const updateStoredFavorite = (id, shouldBeFav) => {
    let favs = getStoredFavorites();
    if (shouldBeFav) {
      if (!favs.includes(id)) favs.push(id);
    } else {
      favs = favs.filter((item) => item !== id);
    }
    localStorage.setItem("user_favorites", JSON.stringify(favs));
  };

  const checkFavStatus = () => {
    const activeId = getCurrentProductId();
    const storedFavs = getStoredFavorites();
    isFav.value = storedFavs.includes(activeId) || props.isFavorite;
  };


  const handleCustomFavEvent = (event) => {
    const activeId = getCurrentProductId();
    if (event.detail && event.detail.id === activeId) {
      isFav.value = event.detail.isFavorite;
    }
  };

  const handleStorageEvent = (event) => {
    if (event.key === "user_favorites") {
      checkFavStatus();
    }
  };

  onMounted(() => {
    checkFavStatus();
    window.addEventListener("favorite-updated", handleCustomFavEvent);
    window.addEventListener("storage", handleStorageEvent);
  });

  onUnmounted(() => {
    window.removeEventListener("favorite-updated", handleCustomFavEvent);
    window.removeEventListener("storage", handleStorageEvent);
  });

  watch(
    () => props.isFavorite,
    (newVal) => {
      isFav.value = newVal;
    }
  );

  watch(selectedVariant, () => {
    checkFavStatus();
  });

  const activeFeaturedImage = computed(() => {
    if (selectedVariant.value && selectedVariant.value.featured_image) {
      return selectedVariant.value.featured_image.fullUrl;
    }
    return (
      props.product.featured_image?.fullUrl || "/storage/2025/no-image.png"
    );
  });

  const formatPrice = computed(() => {
    if (selectedVariant.value) {
      const price = parseFloat(selectedVariant.value.price);
      return isNaN(price) ? "0.00€" : `${price.toFixed(2)}€`;
    }
    const mainPrice = parseFloat(props.product.price);
    return isNaN(mainPrice) ? "0.00€" : `${mainPrice.toFixed(2)}€`;
  });

  const formattedIvaAmount = computed(() => {
    const priceWithVat = parseFloat(
      selectedVariant.value?.priceWithVAT ?? props.product.priceWithVAT
    );
    const priceWithoutVat = parseFloat(
      selectedVariant.value?.price ?? props.product.price
    );

    if (isNaN(priceWithVat) || isNaN(priceWithoutVat)) return "0.00€";

    const ivaAmount = Math.max(0, priceWithVat - priceWithoutVat);

    return `${ivaAmount.toFixed(2)}€`;
  });

  const formattedEcoValue = computed(() => {
    const eco = selectedVariant.value?.ecoValue ?? props.product.ecoValue;
    if (!eco) return null;

    const val = parseFloat(eco);
    if (isNaN(val) || val === 0) return null;

    return `${val.toFixed(2)}€`;
  });

  const hoverImage = computed(() => {
    return props.product.dynamicGalleries?.length
      ? props.product.dynamicGalleries[0].fullUrl
      : null;
  });

  const isAvailable = computed(() => {
    if (selectedVariant.value) {
      return selectedVariant.value.stock > 0;
    }
    return props.product.stock > 0;
  });

  const getCleanVariantName = (name) => {
    if (!name) return "";
    let clean = name.replace(props.product.name, "").replace("-", "").trim();
    if (clean.toLowerCase().includes("6 for the price of 5")) {
      return "Leve 6, Pague 5";
    }
    return clean || name;
  };

  const handleAddToCart = () => {
    if (isAvailable.value) {
      const idToSubmit = getCurrentProductId();
      emit("add-to-cart", idToSubmit, 1);
    }
  };

  const triggerToast = (msg) => {
    favToastMessage.value = msg;
    showFavToast.value = true;
    setTimeout(() => {
      showFavToast.value = false;
    }, 3000);
  };

  const toggleFavorite = () => {
    if (!props.userEmail || props.userEmail.trim() === "") {
      showAuthWarning.value = true;
      return;
    }

    if (isFavLoading.value) return;

    const idToSubmit = getCurrentProductId();
    const payload = { id: idToSubmit };
    const nextFavState = !isFav.value;

    isFav.value = nextFavState;
    updateStoredFavorite(idToSubmit, nextFavState);


    window.dispatchEvent(
      new CustomEvent('favorite-updated', {
        detail: { id: idToSubmit, isFavorite: nextFavState },
      })
    );

    isFavLoading.value = true;

    const handleSuccess = (response) => {
      isFavLoading.value = false;

      if (isFav.value) {
        triggerToast("Produto adicionado à lista de favoritos no teu perfil.");
      } else {
        triggerToast("Produto removido dos favoritos.");
      }

      emit("toggle-favorite", {
        id: idToSubmit,
        isFavorite: isFav.value,
        product: props.product,
        response: response,
      });
    };

    const handleError = (error) => {
      isFavLoading.value = false;
      isFav.value = !nextFavState;
      updateStoredFavorite(idToSubmit, !nextFavState);

      window.dispatchEvent(
        new CustomEvent('favorite-updated', {
          detail: { id: idToSubmit, isFavorite: !nextFavState },
        })
      );

      console.error("Erro ao atualizar favoritos:", error);
    };

    if (window.favoriteProducts) {
      if (nextFavState) {
        window.favoriteProducts.add(payload, handleSuccess, handleError);
      } else {
        window.favoriteProducts.remove(payload, handleSuccess, handleError);
      }
    } else {
      isFavLoading.value = false;

      if (isFav.value) {
        triggerToast("Produto adicionado à lista de favoritos no teu perfil.");
      } else {
        triggerToast("Produto removido dos favoritos.");
      }

      emit("toggle-favorite", {
        id: idToSubmit,
        isFavorite: isFav.value,
        product: props.product,
      });
    }
  };

  const sendBackInStockRequest = (email) => {
    const idToSubmit = getCurrentProductId();

    const payload = {
      id: idToSubmit,
      email: email,
    };

    isSubmitting.value = true;

    if (window.bis && typeof window.bis.set === "function") {
      window.bis.set(
        payload,
        (response) => {
          isSubmitting.value = false;
          showModal.value = false;
          isNotified.value = true;
          inputEmail.value = "";
          emit("notify-back-in-stock", payload);
        },
        (error) => {
          isSubmitting.value = false;
          console.error("Erro ao registar alerta:", error);
        }
      );
    } else {
      isSubmitting.value = false;
      showModal.value = false;
      isNotified.value = true;
      emit("notify-back-in-stock", payload);
    }
  };

  const handleNotifyBackInStock = () => {
    if (props.userEmail && props.userEmail.trim() !== "") {
      sendBackInStockRequest(props.userEmail);
    } else {
      showModal.value = true;
    }
  };

  const submitModalEmail = () => {
    if (!inputEmail.value) return;
    sendBackInStockRequest(inputEmail.value);
  };

  const redirectToLogin = () => {
    window.location.href = "/login";
  };
</script>

<template>
  <div class="product-card" :class="{ 'list-view': props.isListView }">
    <div class="card">
      <div class="content-cards">
        <a
          :href="selectedVariant ? selectedVariant.slug : product.slug"
          :title="product.name"
          class="product-link">
          <div class="div-image" :class="{ 'list-view': props.isListView }">
            <button
              type="button"
              class="btn-favorite"
              :class="{ 'is-active': isFav, 'is-loading': isFavLoading }"
              :disabled="isFavLoading"
              :title="props.userEmail ? 'Adicionar aos Favoritos' : 'Inicie sessão para adicionar aos favoritos'"
              @click.prevent.stop="toggleFavorite">
              <svg
                viewBox="0 0 24 24"
                :fill="isFav ? 'currentColor' : 'none'"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round">
                <path
                  d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12
                  21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </button>

            <template v-if="activeFeaturedImage">
              <div class="image-wrapper">
                <img
                  :src="activeFeaturedImage"
                  :alt="product.name"
                  class="product-image image-main"
                  :class="{ 'has-hover': hoverImage }" />
              </div>
            </template>
            <img
              v-else
              src="/storage/2025/no-image.png"
              alt="Sem imagem disponível"
              class="product-image" />
            <div
              class="stock-badge"
              :class="isAvailable ? 'in-stock' : 'out-of-stock'">
              <span class="stock-dot"></span>
              <span class="stock-text">{{
                  isAvailable ? "Disponível" : "Indisponível"
                }}</span>
            </div>
          </div>
          <div class="product-info" :class="{ 'list-view': props.isListView }">
            <div class="info-header">
              <h3 class="title" :class="{ 'list-view': props.isListView }">
                {{ product.name }}
              </h3>

              <div class="price-wrapper">
                <p class="price" :class="{ 'list-view': props.isListView }">
                  {{ formatPrice }}
                </p>
              </div>
              <div class="tax-badges">
                <span v-if="formattedEcoValue" class="badge badge-eco">
                  Ecovalor: {{ formattedEcoValue }}
                </span>
                <span v-if="formattedIvaAmount !== '0.00€'" class="badge badge-vat">
                  IVA {{ product.iva * 100 }}% ({{ formattedIvaAmount }})
                </span>
              </div>
            </div>
          </div>
        </a>

        <div
          v-if="product.variants && product.variants.length > 0"
          class="variants-container"
          :class="{ 'list-view': props.isListView }">
          <div class="variants-gallery">
            <div
              v-for="variant in product.variants"
              :key="variant.id"
              class="variant-thumb-wrapper"
              @click.stop="selectedVariant = variant">
              <img
                :src="
                  variant.featured_image?.fullUrl ||
                  product.featured_image?.fullUrl
                "
                :alt="variant.name"
                class="variant-thumb"
                :class="{
                  active: selectedVariant && selectedVariant.id === variant.id,
                }" />
              <span class="variant-tooltip">
                {{ getCleanVariantName(variant.name) }}
              </span>
            </div>
          </div>
        </div>

        <template v-if="!props.isListView">
          <button
            v-if="isAvailable"
            type="button"
            class="btn-add-cart"
            @click="handleAddToCart">
            <span class="btn-add-cart-text">Adicionar ao carrinho</span>
          </button>
          <button
            v-else
            type="button"
            class="btn-add-cart btn-notify"
            :class="{ 'is-success': isNotified }"
            :disabled="isNotified || isSubmitting"
            @click="handleNotifyBackInStock">
            <template v-if="!isNotified">
              <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
              <span class="btn-add-cart-text">
                {{ isSubmitting ? "A guardar..." : "Lembrete de disponibilidade" }}
              </span>
            </template>
            <template v-else>
              <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span class="btn-add-cart-text">Aviso Registado!</span>
            </template>
          </button>
        </template>
      </div>
    </div>

    <!-- Modal Stock Notification -->
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
          <div class="modal-card">
            <button type="button" class="modal-close" @click="showModal = false">&times;</button>
            <div class="modal-header">
              <div class="modal-icon-wrapper">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
              </div>
              <h4>Seja o primeiro a saber</h4>
              <p>Insira o seu email para receber notificação assim que o produto voltar ao stock.</p>
            </div>

            <form @submit.prevent="submitModalEmail" class="modal-form">
              <input
                v-model="inputEmail"
                type="email"
                placeholder="Insira o seu email"
                required
                class="modal-input"
              />
              <div class="modal-actions">
                <button type="button" class="btn-cancel" @click="showModal = false">
                  Cancelar
                </button>
                <button type="submit" class="btn-submit" :disabled="isSubmitting">
                  {{ isSubmitting ? "A guardar..." : "Avisar-me" }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Modal Auth Warning -->
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="showAuthWarning" class="modal-overlay" @click.self="showAuthWarning = false">
          <div class="modal-card">
            <button type="button" class="modal-close" @click="showAuthWarning = false">&times;</button>
            <div class="modal-header">
              <div class="modal-icon-wrapper auth-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12
                  21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </div>
              <h4>Iniciar sessão necessário</h4>
              <p>Precisa de iniciar sessão na sua conta para guardar produtos na sua lista de favoritos.</p>
            </div>

            <div class="modal-actions">
              <button type="button" class="btn-cancel" @click="showAuthWarning = false">
                Cancelar
              </button>
              <button type="button" class="btn-submit" @click="redirectToLogin">
                Iniciar Sessão
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <Teleport to="body">
      <Transition name="fade">
        <div v-if="showFavToast" class="toast-notification">
          <span>{{ favToastMessage }}</span>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
  .toast-notification {
    position: fixed;
    font-family: "Montserrat", sans-serif;
    bottom: 24px;
    font-size: 12px;
    right: 24px;
    background-color: #10b981;
    color: #ffffff;
    padding: 12px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    font-weight: 500;
    z-index: 9999;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(10px);
  }
</style>
